import React, { useCallback, useEffect, useState } from 'react';
import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';

/**
 * The WhatsApp / SMS switch, and the figures behind it.
 *
 * Turning WhatsApp off here sends everything by SMS immediately, with no
 * fallback wait — which is what you want at the moment WhatsApp starts
 * misbehaving, and why this is a screen rather than an environment variable
 * someone has to redeploy to change.
 *
 * Three different things can stop WhatsApp being used, and the screen says
 * which, because "the toggle is on but nothing is sending" is otherwise
 * impossible to diagnose:
 *   - WA_ENABLED is false on the server (needs a redeploy)
 *   - credentials are missing (needs the env values)
 *   - the system switched it off itself after a fatal error
 */

const money = (v: any) => `₦${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function NotificationChannels() {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        SuperAdminApis.getNotificationChannels()
            .then((res: any) => {
                if (res?.status) setData(res.data);
                else toast.error(res?.message || 'Could not load notification settings');
            })
            .catch((e: any) => toast.error(e?.response?.data?.message || 'Could not load notification settings'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = (enabled: boolean) => {
        setBusy(true);
        SuperAdminApis.toggleWhatsappChannel({ enabled })
            .then((res: any) => {
                if (res?.status) { toast.success(res.message); load(); }
                // The refusals are the useful part — they say exactly what is
                // missing — so they are shown in full and left on screen.
                else toast.error(res?.message || 'Could not change the channel', { autoClose: 15000 });
            })
            .catch((e: any) => toast.error(e?.response?.data?.message || 'Could not change the channel', { autoClose: 15000 }))
            .finally(() => setBusy(false));
    };

    const clearAutoDisable = () => {
        setBusy(true);
        SuperAdminApis.clearWhatsappAutoDisable()
            .then((res: any) => { toast.success(res?.message || 'Cleared'); load(); })
            .catch(() => toast.error('Could not clear'))
            .finally(() => setBusy(false));
    };

    const eff = data?.effective;
    const active = !!eff?.active;

    return (
        <>
            <AdminSidebar title="Notifications" />
            <div className="relative md:ml-64 bg-[#FAFAFA] min-h-screen">
                <AdminNavbar title="Notification Channels" />

                <div className="px-4 md:px-10 py-6">
                    <h2 className="text-xl font-semibold text-[#19213D]">Notification Channels</h2>
                    <p className="text-sm text-gray-500 mb-6">
                        Choose how customers are told about their shipments. SMS is always the fallback.
                    </p>

                    {loading && !data ? (
                        <div className="flex justify-center py-16">
                            <Oval visible height="40" width="40" color="#E04343" secondaryColor="#eee" ariaLabel="loading" />
                        </div>
                    ) : !data ? null : (
                        <>
                            {/* ── The switch ───────────────────────────── */}
                            <div className="bg-white rounded-lg shadow-sm p-5 mb-5">
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h3 className="font-semibold text-[#19213D]">WhatsApp</h3>
                                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                {active ? 'In use' : 'Not in use'}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 mt-2 max-w-2xl">
                                            {active
                                                ? 'Shipment notifications go out on WhatsApp. If WhatsApp cannot confirm delivery in time, the customer gets an SMS instead — once, never twice.'
                                                : 'Everything is going out by SMS, immediately, with no waiting.'}
                                        </p>
                                        {!active && eff?.reason && (
                                            <p className="text-sm text-orange-700 mt-2">Why: {eff.reason}</p>
                                        )}
                                        {data.changed_by && (
                                            <p className="text-xs text-gray-400 mt-2">
                                                Last changed by {data.changed_by}{data.changed_at ? ` on ${data.changed_at}` : ''}
                                            </p>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => toggle(!data.whatsapp_enabled)}
                                        disabled={busy}
                                        className={`px-5 py-2.5 rounded-md text-sm font-medium text-white disabled:opacity-50 ${data.whatsapp_enabled ? 'bg-gray-700 hover:bg-gray-800' : 'bg-[#E04343] hover:bg-red-600'}`}
                                    >
                                        {busy ? 'Working…' : data.whatsapp_enabled ? 'Turn WhatsApp off' : 'Turn WhatsApp on'}
                                    </button>
                                </div>
                            </div>

                            {/* Set by the system itself, not by a person. */}
                            {data.auto_disabled && (
                                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-5">
                                    <p className="font-semibold text-red-900">WhatsApp was switched off automatically</p>
                                    <p className="text-sm text-red-800 mt-1">
                                        Error {data.auto_disabled.code}: {data.auto_disabled.reason}
                                        {data.auto_disabled.at ? ` (${data.auto_disabled.at})` : ''}
                                    </p>
                                    <p className="text-sm text-red-800 mt-2">
                                        This happens when WhatsApp stops working for everyone — usually a revoked access
                                        token or an unpaid Meta account. Everything is going by SMS. Fix the cause first,
                                        then clear this.
                                    </p>
                                    <button onClick={clearAutoDisable} disabled={busy}
                                        className="mt-3 px-3 py-2 rounded-md bg-white border border-red-300 text-red-700 text-sm hover:bg-red-50 disabled:opacity-50">
                                        Clear and try WhatsApp again
                                    </button>
                                </div>
                            )}

                            {/* What is stopping it being switched on. */}
                            {data.missing?.length > 0 && (
                                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-5">
                                    <p className="font-semibold text-amber-900">Setup is not finished</p>
                                    <p className="text-sm text-amber-800 mt-1">
                                        These values are still missing on the server:{' '}
                                        <strong>{data.missing.join(', ')}</strong>
                                    </p>
                                    <p className="text-sm text-amber-800 mt-2">
                                        WhatsApp cannot be switched on until all of them are set. Without the webhook
                                        values nothing confirms delivery, so every message would also be sent by SMS
                                        and billed twice.
                                    </p>
                                </div>
                            )}

                            {/* ── Figures ──────────────────────────────── */}
                            <h3 className="font-semibold text-[#19213D] mb-3">Last {data.stats?.days ?? 30} days</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                                {(['whatsapp', 'sms'] as const).map((ch) => {
                                    const s = data.stats?.[ch] || {};
                                    return (
                                        <div key={ch} className="bg-white rounded-lg shadow-sm p-4">
                                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">{ch}</p>
                                            <div className="grid grid-cols-2 gap-y-2 text-sm">
                                                <span className="text-gray-500">Sent</span>
                                                <span className="text-right font-medium">{s.total ?? 0}</span>
                                                <span className="text-gray-500">Delivered</span>
                                                <span className="text-right font-medium">
                                                    {/* SMS has no delivery receipts, so this is only meaningful on WhatsApp. */}
                                                    {ch === 'sms' ? '—' : (s.delivery_rate === null || s.delivery_rate === undefined ? '—' : `${s.delivery_rate}%`)}
                                                </span>
                                                <span className="text-gray-500">Failed</span>
                                                <span className="text-right font-medium">{s.failed ?? 0}</span>
                                                <span className="text-gray-500">Cost</span>
                                                <span className="text-right font-medium">{money(s.cost)}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
                                <div className="bg-white rounded-lg shadow-sm p-4">
                                    <p className="text-xs uppercase tracking-wide text-gray-500">Fell back to SMS</p>
                                    <p className="text-xl font-semibold text-gray-800">{data.stats?.fallbacks ?? 0}</p>
                                    <p className="text-[11px] text-gray-400">
                                        {data.stats?.fallback_rate === null || data.stats?.fallback_rate === undefined
                                            ? 'each one is paid for twice'
                                            : `${data.stats.fallback_rate}% — each one is paid for twice`}
                                    </p>
                                </div>
                                <div className="bg-white rounded-lg shadow-sm p-4">
                                    <p className="text-xs uppercase tracking-wide text-gray-500">Opted in</p>
                                    <p className="text-xl font-semibold text-gray-800">{data.contacts?.opted_in ?? 0}</p>
                                </div>
                                <div className="bg-white rounded-lg shadow-sm p-4">
                                    <p className="text-xs uppercase tracking-wide text-gray-500">Opted out</p>
                                    <p className="text-xl font-semibold text-gray-800">{data.contacts?.opted_out ?? 0}</p>
                                </div>
                                <div className="bg-white rounded-lg shadow-sm p-4">
                                    <p className="text-xs uppercase tracking-wide text-gray-500">Not on WhatsApp</p>
                                    <p className="text-xl font-semibold text-gray-800">{data.contacts?.unreachable ?? 0}</p>
                                    <p className="text-[11px] text-gray-400">skipped automatically</p>
                                </div>
                            </div>

                            {data.stats?.top_errors?.length > 0 && (
                                <div className="bg-white rounded-lg shadow-sm p-4">
                                    <p className="font-semibold text-sm mb-3">Most common errors</p>
                                    <table className="w-full text-sm">
                                        <thead className="text-xs uppercase text-gray-500 bg-gray-50">
                                            <tr>
                                                <th className="px-3 py-2 text-left">Code</th>
                                                <th className="px-3 py-2 text-left">Meaning</th>
                                                <th className="px-3 py-2 text-right">Times</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.stats.top_errors.map((e: any) => (
                                                <tr key={e.error_code} className="border-b last:border-b-0">
                                                    <td className="px-3 py-2 font-medium">{e.error_code}</td>
                                                    <td className="px-3 py-2 text-gray-600">{e.title || '—'}</td>
                                                    <td className="px-3 py-2 text-right">{e.n}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <ToastContainer position="top-right" autoClose={5000} newestOnTop closeOnClick pauseOnHover />
        </>
    );
}

export default NotificationChannels;
